import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Pause, Play, RotateCcw } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { api, errorMessage } from "../lib/api";

const COLORS = { hinge: "#3B82F6", drill: "#00E5FF", channel: "#A855F7", profile: "#E67E22" };
const KIND = { hinge: "Furação 35 mm", drill: "Furação 8 mm", channel: "Canal / rebaixo", profile: "Contorno" };
const SPEEDS = [1, 4, 16];

const opLen = (pts) => pts.slice(1).reduce((a, p, i) => a + Math.hypot(p[0] - pts[i][0], p[1] - pts[i][1]), 0);
export const fmtTime = (s) => { const m = Math.floor(s / 60), h = Math.floor(m / 60); return h ? `${h}h ${String(m % 60).padStart(2, "0")}min` : `${m}min ${String(Math.round(s % 60)).padStart(2, "0")}s`; };

// part of a polyline up to distance d
const partial = (pts, d) => {
  const out = [pts[0]];
  let acc = 0;
  for (let i = 1; i < pts.length; i++) {
    const [ax, ay] = pts[i - 1], [bx, by] = pts[i], s = Math.hypot(bx - ax, by - ay);
    if (acc + s >= d) { const t = s ? (d - acc) / s : 0; out.push([ax + (bx - ax) * t, ay + (by - ay) * t]); return out; }
    acc += s; out.push(pts[i]);
  }
  return out;
};

const useSimData = (open, projectId, sheet) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!open) { return; }
    setData(null); setError(null);
    api.get(`/projects/${projectId}/gcode-sim`, { params: { sheet } }).then((r) => setData(r.data)).catch((e) => setError(errorMessage(e)));
  }, [open, projectId, sheet]);
  return { data, error };
};

const SimSvg = ({ data, ops, step, frac }) => {
  const { length: SL, width: SW } = data.sheet;
  const y = (v) => SL - v;
  const done = new Set(ops.slice(0, step).filter((o) => o.kind === "profile" && /final|Passada 2/.test(o.label)).map((o) => o.num));
  const cur = ops[step];
  const curPts = cur && (cur.pts.length > 1 ? partial(cur.pts, opLen(cur.pts) * frac) : cur.pts);
  const head = curPts?.[curPts.length - 1];
  const line = (pts) => pts.map((p) => `${p[0]},${y(p[1])}`).join(" ");
  return (
    <svg data-testid="gcode-sim-svg" viewBox={`-20 -20 ${SW + 40} ${SL + 40}`} className="w-full h-auto max-h-[62vh]">
      <rect x="0" y="0" width={SW} height={SL} fill="#2A2118" stroke="#6B7280" strokeWidth="4" />
      {data.placed.map((p) => (
        <g key={p.num}>
          <rect x={p.x} y={y(p.y + p.h)} width={p.w} height={p.h} fill={done.has(p.num) ? "#10B98155" : "#3A2E22"} stroke="#8B6B4A" strokeWidth="3" />
          <text x={p.x + p.w / 2} y={y(p.y + p.h / 2)} fill="#D1D5DB" fontSize={Math.min(60, p.w / 3, p.h / 2)} textAnchor="middle" dominantBaseline="middle">{p.num}</text>
        </g>
      ))}
      {ops.slice(0, step).map((o, i) => o.pts.length > 1
        ? <polyline key={i} points={line(o.pts)} fill="none" stroke={COLORS[o.kind]} strokeWidth="5" opacity="0.8" />
        : <circle key={i} cx={o.pts[0][0]} cy={y(o.pts[0][1])} r={o.kind === "hinge" ? 17.5 : 8} fill={COLORS[o.kind]} />)}
      {curPts && curPts.length > 1 && <polyline points={line(curPts)} fill="none" stroke="#FFFFFF" strokeWidth="7" />}
      {head && <circle data-testid="gcode-sim-head" cx={head[0]} cy={y(head[1])} r="22" fill="none" stroke="#FFFFFF" strokeWidth="6" />}
    </svg>
  );
};

const useAnimation = (ops, playing, speed, setPlaying) => {
  const [pos, setPos] = useState({ step: 0, frac: 0 });
  const raf = useRef(null);
  useEffect(() => {
    if (!playing || !ops.length) { return undefined; }
    let last = performance.now();
    const tick = (t) => {
      const dt = (t - last) / 1000; last = t;
      setPos((o) => {
        const op = ops[o.step];
        if (!op) { setPlaying(false); return o; }
        const dur = op.pts.length > 1 ? Math.max(0.35, opLen(op.pts) / 2500) : 0.12;
        const frac = o.frac + (dt * speed) / dur;
        if (frac < 1) return { step: o.step, frac };
        if (o.step + 1 >= ops.length) { setPlaying(false); return { step: ops.length, frac: 0 }; }
        return { step: o.step + 1, frac: 0 };
      });
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [playing, speed, ops, setPlaying]);
  return [pos, setPos];
};

export const GcodeSim = ({ open, onOpenChange, projectId, sheet }) => {
  const { data, error } = useSimData(open, projectId, sheet);
  const ops = useMemo(() => data?.ops || [], [data]);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(4);
  const [pos, setPos] = useAnimation(ops, playing, speed, setPlaying);
  useEffect(() => { setPos({ step: 0, frac: 0 }); setPlaying(Boolean(data)); }, [data, setPos]);
  const cur = ops[Math.min(pos.step, ops.length - 1)];
  const finished = pos.step >= ops.length;
  return (
    <Dialog open={open} onOpenChange={(o) => { setPlaying(false); onOpenChange(o); }}>
      <DialogContent data-testid="gcode-sim-dialog" className="max-w-3xl bg-[#181B21] border-[#2D333F] text-[#F3F4F6]">
        <DialogHeader><DialogTitle>Simulação do corte · Chapa {sheet}</DialogTitle></DialogHeader>
        {error && <p data-testid="gcode-sim-error" className="text-red-400 text-sm">{error}</p>}
        {!data && !error && <div className="flex items-center gap-2 text-[#9CA3AF] p-8"><Loader2 className="w-5 h-5 animate-spin" /> Gerando simulação…</div>}
        {data && (
          <div className="grid md:grid-cols-[1fr_15rem] gap-4">
            <div className="rounded-lg bg-[#0F1115] p-2"><SimSvg data={data} ops={ops} step={Math.min(pos.step, ops.length)} frac={pos.frac} /></div>
            <div className="space-y-3 text-sm">
              <p data-testid="gcode-sim-step" className="font-mono text-[#F39C12]">Passo {Math.min(pos.step + 1, ops.length)} de {ops.length}</p>
              <div data-testid="gcode-sim-time" className="rounded-md bg-[#0F1115] px-3 py-2 font-mono text-xs">
                <p>Tempo estimado da chapa: <b className="text-[#F3F4F6]">{fmtTime(data.total_time)}</b></p>
                <p className="text-[#9CA3AF]">Decorrido: {fmtTime(finished ? data.total_time : (ops[pos.step - 1]?.t || 0) + ((cur?.t || 0) - (ops[pos.step - 1]?.t || 0)) * pos.frac)}</p>
              </div>
              {cur && !finished && (
                <div data-testid="gcode-sim-current" className="rounded-md border border-[#2D333F] p-3 space-y-1">
                  <p className="font-semibold" style={{ color: COLORS[cur.kind] }}>{KIND[cur.kind]} · T{cur.tool}</p>
                  <p>Peça {cur.num} · {data.names[cur.num]}</p>
                  <p className="text-[#9CA3AF]">{cur.label}{cur.tabs ? ` · ${cur.tabs} pontes de fixação` : ""}</p>
                </div>
              )}
              {finished && <p data-testid="gcode-sim-done" className="text-[#10B981] font-semibold">Corte da chapa concluído</p>}
              <input data-testid="gcode-sim-slider" type="range" min="0" max={ops.length} value={Math.min(pos.step, ops.length)} className="w-full accent-[#E67E22]"
                onChange={(e) => { setPlaying(false); setPos({ step: +e.target.value, frac: 0 }); }} />
              <div className="flex flex-wrap gap-2">
                <Button size="sm" data-testid="gcode-sim-play" onClick={() => { if (finished) setPos({ step: 0, frac: 0 }); setPlaying((o) => !o); }} className="bg-[#E67E22] hover:bg-[#D35400] text-black">
                  {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                </Button>
                <Button size="sm" variant="outline" data-testid="gcode-sim-restart" onClick={() => { setPos({ step: 0, frac: 0 }); setPlaying(true); }} className="border-[#2D333F] bg-transparent"><RotateCcw className="w-4 h-4" /></Button>
                {SPEEDS.map((s) => (
                  <Button key={s} size="sm" variant="outline" data-testid={`gcode-sim-speed-${s}`} onClick={() => setSpeed(s)}
                    className={`border-[#2D333F] bg-transparent ${speed === s ? "text-[#E67E22] border-[#E67E22]" : ""}`}>{s}x</Button>
                ))}
              </div>
              <div className="space-y-1 text-xs text-[#9CA3AF]">
                {Object.entries(KIND).map(([k, l]) => <p key={k}><span className="inline-block w-3 h-3 rounded-sm mr-2 align-middle" style={{ background: COLORS[k] }} />{l}</p>)}
                <p><span className="inline-block w-3 h-3 rounded-sm mr-2 align-middle bg-[#10B98155]" />Peça já cortada</p>
                <p className="pt-1">Tempo calculado pelas velocidades de cada fresa no G-code; deslocamentos rápidos estimados em 20 m/min e troca de ferramenta em 15 s.</p>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
